// The <Rules> options a base policy can set, keyed by Microsoft's option number. Options 1, 7 and 8
// are left out, as Microsoft documents them as unsupported, and 5 because it is reserved and does
// nothing. Microsoft marks only some as valid in a supplemental policy too.
export const POLICY_RULE_OPTION = {
  EnabledUmci: 0,
  RequiredWhql: 2,
  EnabledAuditMode: 3,
  DisabledFlightSigning: 4,
  EnabledUnsignedSystemIntegrityPolicy: 6,
  EnabledAdvancedBootOptionsMenu: 9,
  EnabledBootAuditOnFailure: 10,
  DisabledScriptEnforcement: 11,
  RequiredEnforceStoreApplications: 12,
  EnabledManagedInstaller: 13,
  EnabledIntelligentSecurityGraphAuthorization: 14,
  EnabledInvalidateEAsOnReboot: 15,
  EnabledUpdatePolicyNoReboot: 16,
  EnabledAllowSupplementalPolicies: 17,
  DisabledRuntimeFilePathRuleProtection: 18,
  EnabledDynamicCodeSecurity: 19,
  EnabledRevokedExpiredAsUnsigned: 20,
};

const POLICY_RULE_OPTION_MAPPING = [
  {
    value: POLICY_RULE_OPTION.EnabledUmci,
    label: 'Enabled:UMCI',
    description: 'Also restrict user-mode executables and scripts, not just kernel drivers.',
    isDefault: true,
    warning: selected =>
      selected.includes(POLICY_RULE_OPTION.EnabledUmci)
        ? null
        : 'Without UMCI only kernel drivers are controlled. Every user-mode app and script runs whether the policy allows it or not.',
  },
  {
    value: POLICY_RULE_OPTION.RequiredWhql,
    label: 'Required:WHQL',
    description: 'Only allow WHQL-signed kernel drivers; legacy drivers stop loading.',
  },
  {
    value: POLICY_RULE_OPTION.EnabledAuditMode,
    label: 'Enabled:Audit Mode',
    description: 'Log what would be blocked but let it run.',
    emphasis: 'Remove this option to enforce.',
    isDefault: true,
  },
  {
    value: POLICY_RULE_OPTION.DisabledFlightSigning,
    label: 'Disabled:Flight Signing',
    description: "Don't trust binaries from Windows Insider (flight-signed) builds.",
    isDefault: true,
  },
  {
    value: POLICY_RULE_OPTION.EnabledUnsignedSystemIntegrityPolicy,
    label: 'Enabled:Unsigned System Integrity Policy',
    description:
      'The policy may stay unsigned. Without it, this policy and its supplementals must be signed.',
    isDefault: true,
    validInSupplemental: true,
  },
  {
    value: POLICY_RULE_OPTION.EnabledAdvancedBootOptionsMenu,
    label: 'Enabled:Advanced Boot Options Menu',
    description: 'Keep the F8 preboot menu available to someone at the device.',
    isDefault: true,
  },
  {
    value: POLICY_RULE_OPTION.EnabledBootAuditOnFailure,
    label: 'Enabled:Boot Audit on Failure',
    description:
      'If a boot-critical driver is blocked in enforced mode, fall back to audit mode so Windows still starts.',
  },
  {
    value: POLICY_RULE_OPTION.DisabledScriptEnforcement,
    label: 'Disabled:Script Enforcement',
    description: "Don't enforce the policy on PowerShell, wscript, cscript, mshta and MSXML.",
    warning: selected =>
      selected.includes(POLICY_RULE_OPTION.DisabledScriptEnforcement)
        ? 'Scripts run unrestricted and PowerShell leaves Constrained Language Mode, a common way to run code the policy would otherwise block.'
        : null,
  },
  {
    value: POLICY_RULE_OPTION.RequiredEnforceStoreApplications,
    label: 'Required:Enforce Store Applications',
    description: 'Apply the policy to Store (UWP / packaged) apps too.',
    isDefault: true,
  },
  {
    value: POLICY_RULE_OPTION.EnabledManagedInstaller,
    label: 'Enabled:Managed Installer',
    description:
      'Allow apps installed by a managed installer (e.g. Intune Management Extension, ConfigMgr).',
    isDefault: true,
    validInSupplemental: true,
  },
  {
    value: POLICY_RULE_OPTION.EnabledIntelligentSecurityGraphAuthorization,
    label: 'Enabled:ISG Authorization',
    description: "Allow files Microsoft's Intelligent Security Graph rates as known good.",
    validInSupplemental: true,
  },
  {
    value: POLICY_RULE_OPTION.EnabledInvalidateEAsOnReboot,
    label: 'Enabled:Invalidate EAs on Reboot',
    description: 'With ISG (14): re-check the reputation of files the ISG allowed earlier.',
    warning: selected =>
      selected.includes(POLICY_RULE_OPTION.EnabledInvalidateEAsOnReboot) &&
      !selected.includes(POLICY_RULE_OPTION.EnabledIntelligentSecurityGraphAuthorization)
        ? 'This does nothing without Enabled:ISG Authorization (14).'
        : null,
  },
  {
    value: POLICY_RULE_OPTION.EnabledUpdatePolicyNoReboot,
    label: 'Enabled:Update Policy No Reboot',
    description: 'Apply policy updates without a reboot.',
    isDefault: true,
  },
  {
    value: POLICY_RULE_OPTION.EnabledAllowSupplementalPolicies,
    label: 'Enabled:Allow Supplemental Policies',
    description: 'Let supplemental policies extend this base policy.',
    isDefault: true,
  },
  {
    value: POLICY_RULE_OPTION.DisabledRuntimeFilePathRuleProtection,
    label: 'Disabled:Runtime FilePath Rule Protection',
    description:
      'Let path rules match folders that non-admins can write to (turns off the admin-writable check).',
    validInSupplemental: true,
    warning: selected =>
      selected.includes(POLICY_RULE_OPTION.DisabledRuntimeFilePathRuleProtection)
        ? 'Any user can drop a file into a folder a path rule allows and run it.'
        : null,
  },
  {
    value: POLICY_RULE_OPTION.EnabledDynamicCodeSecurity,
    label: 'Enabled:Dynamic Code Security',
    description:
      'Enforce the policy on .NET dynamic code and dynamically loaded libraries (no audit mode for this).',
    validInSupplemental: true,
  },
  {
    value: POLICY_RULE_OPTION.EnabledRevokedExpiredAsUnsigned,
    label: 'Enabled:Revoked Expired As Unsigned',
    description:
      'Treat user-mode files signed with revoked, or expired lifetime-signing, certificates as unsigned.',
    validInSupplemental: true,
  },
];

export const getPolicyRuleOptionLabel = value =>
  POLICY_RULE_OPTION_MAPPING.find(item => item.value === value)?.label ?? '';

export const getPolicyRuleOptionOptions = () =>
  POLICY_RULE_OPTION_MAPPING.map(({ value, label, description, emphasis }) => ({
    value,
    label,
    description,
    emphasis,
  }));

export const isPolicyRuleOptionValidInSupplemental = value =>
  !!POLICY_RULE_OPTION_MAPPING.find(item => item.value === value)?.validInSupplemental;

// Settings Microsoft advises against, worth a caution on the option while the selection holds them.
export const getPolicyRuleOptionWarning = (value, selected) =>
  POLICY_RULE_OPTION_MAPPING.find(item => item.value === value)?.warning?.(selected) ?? null;

export const getDefaultPolicyRuleOptions = () =>
  POLICY_RULE_OPTION_MAPPING.filter(item => item.isDefault).map(item => item.value);
